import { ChevronRightIcon } from "lucide-react"
import type { CSSProperties } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import type { SidebarLink } from "@/constants/sidebar-links"
import { usePermission } from "@/contexts/permission"
import { useWhitelabel } from "@/contexts/whitelabel"
import { readableTextColor } from "@/utils/color-contrast"

interface AppSidebarMenuProps {
  sidebarLinks: SidebarLink[]
}
export const AppSidebarMenu = ({
  sidebarLinks,
}: AppSidebarMenuProps) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { hasPermission } = usePermission()
  const { isMobile, setOpenMobile } = useSidebar()
  const { configuration } = useWhitelabel()
  // Fundo na cor da marca; o texto sai do contraste com ela — marca com
  // fundo branco deixava o item ativo branco no branco.
  const activeItemStyle: CSSProperties = {
    backgroundColor: "var(--whitelabel-background)",
    color: readableTextColor(
      configuration?.visualPreferences?.layout?.colors
        ?.background
    ),
  }

  const handleMenuClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <SidebarGroup className="space-y-1">
      <SidebarMenu className="space-y-1">
        {sidebarLinks.map((link) => {
          const isActive =
            link.href === pathname ||
            pathname.includes(link.mainPath)
          if (!hasPermission([...link.permissionRoles]))
            return null
          if (link?.subLinks && link.subLinks.length > 0) {
            return (
              <Collapsible
                asChild
                defaultOpen={isActive}
                className="group/collapsible"
                key={link.href}
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip={t(link.tooltip)}
                      isActive={isActive}
                      className="rounded-lg transition-all duration-200"
                      style={
                        isActive
                          ? activeItemStyle
                          : undefined
                      }
                    >
                      <link.icon className="h-4 w-4" />
                      <span className="font-medium">
                        {t(link.label)}
                      </span>
                      <ChevronRightIcon className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="ml-4 space-y-1 border-l border-border/50 pl-3 pt-2">
                      {link?.subLinks?.map((subLink) => {
                        const isSubLinkActive =
                          subLink.href === pathname ||
                          subLink.mainPath.includes(
                            pathname.split("/")[2]
                          )
                        return (
                          <SidebarMenuSubItem
                            key={subLink.href}
                          >
                            <SidebarMenuSubButton
                              className="w-full rounded-md transition-all duration-200"
                              asChild
                              isActive={isSubLinkActive}
                              style={
                                isSubLinkActive
                                  ? activeItemStyle
                                  : undefined
                              }
                            >
                              <Link
                                to={subLink.href}
                                onClick={handleMenuClick}
                              >
                                <span>
                                  {t(subLink.label)}
                                </span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        )
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          }
          return (
            <SidebarMenuItem key={link.href}>
              <SidebarMenuButton
                asChild
                tooltip={t(link.tooltip)}
                isActive={isActive}
                className="rounded-lg transition-all duration-200"
                style={
                  isActive ? activeItemStyle : undefined
                }
              >
                <Link
                  to={link.href}
                  onClick={handleMenuClick}
                >
                  <link.icon className="h-4 w-4" />
                  <span className="font-medium">
                    {t(link.label)}
                  </span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
